import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import { Label } from "@lawline/ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@lawline/ui/components/select";
import { Skeleton } from "@lawline/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";

import { formatDateTime } from "@platform/fmt";
import { webhookDeliveriesQueryOptions, webhookEndpointsQueryOptions } from "../webhooks.queries";
import {
	WEBHOOK_DELIVERY_STATUSES,
	type WebhookDeliveryStatus,
	type WebhookEndpoint,
} from "../webhooks.types";
import { lastResponse } from "../webhooks.utils";
import { WebhookDeliveryDetail } from "./webhook-delivery-detail";
import { WebhookDeliveryStatusBadge } from "./webhook-delivery-status-badge";

const ALL = "all";

export function WebhookDeliveriesTable({ endpoint }: { endpoint: WebhookEndpoint }) {
	const t = useTranslations("webhooks.deliveries");
	const [filter, setFilter] = useState<string>(ALL);
	const [openId, setOpenId] = useState<string | null>(null);
	const status = filter === ALL ? null : (filter as WebhookDeliveryStatus);
	const deliveries = useInfiniteQuery(webhookDeliveriesQueryOptions(endpoint.id, status));
	const queryClient = useQueryClient();
	const pending = useRef<Set<string>>(new Set());
	// A delivery that leaves pending moves the endpoint's last success or failure, which the summary
	// above reads from the endpoint list, so that list is read again.
	useEffect(() => {
		if (!deliveries.data) return;
		const now = new Set(
			deliveries.data.pages
				.flatMap((page) => page.data)
				.filter((delivery) => delivery.status === "pending")
				.map((delivery) => delivery.id),
		);
		const settled = [...pending.current].some((id) => !now.has(id));
		pending.current = now;
		if (settled) {
			void queryClient.invalidateQueries({
				queryKey: webhookEndpointsQueryOptions.queryKey,
				exact: true,
			});
		}
	}, [deliveries.data, queryClient]);
	const rows = deliveries.data?.pages.flatMap((page) => page.data) ?? [];

	return (
		<section className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 className="text-lg font-semibold">{t("title")}</h2>
				<div className="flex items-center gap-2">
					<Label htmlFor="webhook-delivery-filter" className="text-muted-foreground text-sm">
						{t("filterLabel")}
					</Label>
					<Select value={filter} onValueChange={setFilter}>
						<SelectTrigger id="webhook-delivery-filter" size="sm" className="w-36">
							<SelectValue />
						</SelectTrigger>
						<SelectContent position="popper">
							<SelectItem value={ALL}>{t("filterAll")}</SelectItem>
							{WEBHOOK_DELIVERY_STATUSES.map((value) => (
								<SelectItem key={value} value={value}>
									{t(`status.${value}`)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{deliveries.isPending ? (
				<Skeleton className="h-24 w-full" />
			) : deliveries.error ? (
				<p role="alert" className="text-destructive text-sm">
					{t("error")}
				</p>
			) : (
				<div className="overflow-x-auto rounded-md border contain-inline-size">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("columns.event")}</TableHead>
								<TableHead>{t("columns.status")}</TableHead>
								<TableHead className="hidden sm:table-cell">{t("columns.attempts")}</TableHead>
								<TableHead>{t("columns.response")}</TableHead>
								<TableHead className="hidden md:table-cell">{t("columns.created")}</TableHead>
								<TableHead className="hidden md:table-cell">{t("columns.next")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} className="text-muted-foreground h-20 text-center text-sm">
										{t("emptyState")}
									</TableCell>
								</TableRow>
							) : (
								rows.map((delivery) => (
									<TableRow key={delivery.id}>
										<TableCell>
											<button
												type="button"
												onClick={() => setOpenId(delivery.id)}
												className="font-mono text-xs font-medium hover:underline"
											>
												{delivery.eventType}
											</button>
										</TableCell>
										<TableCell>
											<div className="flex flex-wrap items-center gap-1">
												<WebhookDeliveryStatusBadge status={delivery.status} />
												{delivery.manualPending && (
													<span className="text-muted-foreground text-xs">
														{t("manualPending")}
													</span>
												)}
											</div>
										</TableCell>
										<TableCell className="hidden sm:table-cell">{delivery.attemptCount}</TableCell>
										<TableCell className="font-mono text-xs">{lastResponse(delivery)}</TableCell>
										<TableCell className="hidden md:table-cell">
											{formatDateTime(delivery.createdAt)}
										</TableCell>
										<TableCell className="hidden md:table-cell">
											{formatDateTime(delivery.nextAttemptAt)}
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</div>
			)}

			{deliveries.hasNextPage && (
				<Button
					variant="outline"
					size="sm"
					className="self-center"
					disabled={deliveries.isFetchingNextPage}
					onClick={() => void deliveries.fetchNextPage()}
				>
					{t("loadMore")}
				</Button>
			)}

			{openId && (
				<WebhookDeliveryDetail
					endpoint={endpoint}
					deliveryId={openId}
					onClose={() => setOpenId(null)}
				/>
			)}
		</section>
	);
}
